import {message, useIntl} from '@sail/intl';
import {urlRedirect} from '@stripe-internal/safe-links';
import {view, type CssProperties} from '@sail/ui';
import {isEmbedded} from 'src/utils/utils';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {ScreenContentLayout} from 'src/v2/components/screen/ScreenContentLayout';
import {usePaymentPollingAnyCrypto} from 'src/v2/components/anyCrypto/usePaymentPollingAnyCrypto';
import {useProgressScreenContent} from 'src/v2/components/anyCrypto/progress/useProgressScreenContent';
import {ProgressStatusIcon} from 'src/v2/components/anyCrypto/progress/ProgressStatusIcon';
import {ProgressHeader} from 'src/v2/components/anyCrypto/progress/ProgressHeader';
import {StepProgressIndicator} from 'src/v2/components/common/StepProgressIndicator';
import {useSelector} from 'src/v2/state/store';

const messages = {
  backButton: message<{
    businessName: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.any_crypto.processing.back_button',
    description: 'Button to return to merchant site',
    defaultMessage: 'Back to {businessName}',
  }),
};

const embeddedStyles: CssProperties = {
  paddingBottom: 'space.300',
};

export const AnyCryptoProgressScreen = () => {
  const sessionState = useSelector((state) => state.state);
  // Skip polling when already complete — it would redirect to the merchant
  // before the balance message shows.
  usePaymentPollingAnyCrypto({
    autoStart: sessionState !== 'fulfillment_complete',
  });
  const intl = useIntl();
  const {statusIconVariant, title, subtitle, steps} =
    useProgressScreenContent();

  const returnUrl = useSelector((state) => state.paymentDetails.returnUrl);
  const businessName = useSelector(
    (state) => state.merchantBranding.businessName,
  );

  return (
    <ScreenContentLayout
      hideTitle
      bottom={
        !isEmbedded() ? (
          <BrandedButton onClick={() => urlRedirect(returnUrl)}>
            {intl.formatMessage(messages.backButton, {businessName})}
          </BrandedButton>
        ) : null
      }
    >
      <view.div
        data-testid="any-crypto-progress-screen"
        css={{stack: 'y', gap: 'large', ...(isEmbedded() && embeddedStyles)}}
      >
        <ProgressStatusIcon statusIconVariant={statusIconVariant} />
        <ProgressHeader title={title} subtitle={subtitle} />
        {steps.length > 0 ? <StepProgressIndicator steps={steps} /> : null}
      </view.div>
    </ScreenContentLayout>
  );
};
